import {ErrorBoundary} from '@sentry/react';
import PropTypes from 'prop-types';
import React, {Suspense, lazy, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import UploadsSidebar from './UploadsSidebar.jsx';
import {LeftDrawer, RightDrawer} from './common/Drawers.jsx';
import {showLeftDrawer, showRightDrawer} from '@app/actions/ui';
import LoadingMessage from '@app/components/common/LoadingMessage';
import {DRAWERS} from '@app/constants';
import CollectionsSidebar from '@components/CollectionsSidebar';
import ErrorMessage from '@components/common/ErrorMessage';

const SettingsSidebar = lazy(
  () =>
    /* webpackChunkName: "sidebar-settings" */ import('./SettingsSidebar.jsx')
);

const StatusDrawer = ({hasError, side}) => {
  const drawerList = {
    left: LeftDrawer,
    right: RightDrawer,
  };

  const SideDrawer = drawerList[side];

  /* istanbul ignore next */
  if (hasError) {
    return (
      <SideDrawer open>
        <ErrorMessage />
      </SideDrawer>
    );
  }

  return (
    <SideDrawer open>
      <LoadingMessage />
    </SideDrawer>
  );
};

StatusDrawer.propTypes = {
  hasError: PropTypes.bool,
  side: PropTypes.oneOf(['left', 'right']).isRequired,
};

const Sidebars = ({
  openDrawers: {left, right},
  showLeftDrawer,
  showRightDrawer,
}) => {
  const [componentsToLoad, setComponentsToLoad] = useState([]);

  const lazyloadLeftSideComponentsMap = {
    [DRAWERS.SETTINGS]: SettingsSidebar,
  };

  useEffect(() => {
    [left, right].forEach(side => {
      if (Boolean(side) && !componentsToLoad.includes(side)) {
        setComponentsToLoad(prevState => [...prevState, side]);
      }
    });
  }, [componentsToLoad, left, right]);

  return (
    <React.Fragment>
      <CollectionsSidebar
        open={left === DRAWERS.COLLECTIONS}
        onClose={() => {
          showLeftDrawer('');
        }}
      />
      <UploadsSidebar
        open={left === DRAWERS.UPLOADS}
        onClose={() => {
          showLeftDrawer('');
        }}
      />
      {componentsToLoad.map(component => {
        const SidebarComponent = lazyloadLeftSideComponentsMap[component];

        return (
          <React.Fragment key={component}>
            {Boolean(SidebarComponent) && (
              <ErrorBoundary
                fallback={
                  <StatusDrawer
                    hasError
                    side={left === component ? 'left' : 'right'}
                  />
                }
              >
                <Suspense
                  fallback={
                    <StatusDrawer
                      side={left === component ? 'left' : 'right'}
                    />
                  }
                >
                  <SidebarComponent
                    onClose={
                      left === component
                        ? () => showLeftDrawer('')
                        : () => showRightDrawer('')
                    }
                    open={left === component || right === component}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

Sidebars.propTypes = {
  openDrawers: PropTypes.shape({
    left: PropTypes.string,
    right: PropTypes.string,
  }).isRequired,
  showLeftDrawer: PropTypes.func.isRequired,
  showRightDrawer: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  openDrawers: state.ui.drawers,
});

const mapDispatchProps = {
  showLeftDrawer,
  showRightDrawer,
};

export default connect(mapStateToProps, mapDispatchProps)(Sidebars);
